/**
 * Catalog data source.
 *
 *   Priority order:
 *     1. API   ({REACT_APP_API_URL}/categories, /treatments, /settings/web-content-overrides)
 *     2. STATIC catalog bundled in `src/data/treatments.js`
 *
 *   • If the API succeeds with a non-empty list → use API data.
 *   • If the API fails, times out, returns empty, or is not configured → fall
 *     back silently to the static catalog. The site never shows a blank
 *     catalog, never throws, never blocks first paint.
 *
 *   The detail page applies a *hybrid merge*: API fields take precedence,
 *   long-form content (`whatIs`, `procedure`, `faq`, `risks`, `whyVietnam`,
 *   etc.) that isn't yet in the database is supplemented from the static
 *   entry with the same slug — preserving SEO depth and reading flow.
 */
import { apiGet, isApiConfigured } from './publicApi';
import {
  CATEGORIES as STATIC_CATEGORIES,
  TREATMENTS as STATIC_TREATMENTS,
} from '../data/treatments';

// ── Normalisers ──────────────────────────────────────────────────────────────
// The API may use snake_case or different field names — keep one canonical
// shape the rest of the app already understands (mirrors data/treatments.js).

const normaliseCategory = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const slug = raw.slug || raw.id;
  if (!slug) return null;
  return {
    slug,
    name: raw.name || raw.title || slug,
    description: raw.description || raw.summary || '',
    color: raw.color || '#1FA7A1',
    icon: raw.icon || null,
    // Keep the raw record so callers can read fields we don't know about.
    _raw: raw,
  };
};

const normaliseTreatment = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const slug = raw.slug || raw.id;
  if (!slug) return null;
  return {
    slug,
    name: raw.name || raw.title || slug,
    category: raw.category || raw.category_slug || raw.categorySlug || '',
    image: raw.image || raw.image_url || raw.imageUrl || '',
    shortDesc: raw.short_desc || raw.shortDesc || raw.description || '',
    startingPrice: raw.starting_price || raw.startingPrice || '',
    recoveryTime: raw.recovery_time || raw.recoveryTime || '',
    accent: raw.accent || { from: '#1FA7A1', to: '#0F8783' },
    flagship: Boolean(raw.flagship ?? raw.is_flagship),
    seo: raw.seo || null,
    // Long-form (may or may not be in API yet)
    whatIs: raw.what_is || raw.whatIs || null,
    whoFor: raw.who_for || raw.whoFor || null,
    benefits: raw.benefits || null,
    procedure: raw.procedure || null,
    recovery: raw.recovery || null,
    risks: raw.risks || null,
    stayInVietnam: raw.stay_in_vietnam || raw.stayInVietnam || null,
    priceGuide: raw.price_guide || raw.priceGuide || null,
    pricingFactors: raw.pricing_factors || raw.pricingFactors || null,
    whyVietnam: raw.why_vietnam || raw.whyVietnam || null,
    whyVietClinics: raw.why_vietclinics || raw.whyVietClinics || null,
    faq: raw.faq || null,
    related: raw.related || raw.related_slugs || null,
    _raw: raw,
  };
};

// ── Fetchers ────────────────────────────────────────────────────────────────

export const fetchCategories = async () => {
  const res = await apiGet('/categories');
  if (!res.ok) return { source: 'static', categories: STATIC_CATEGORIES, reason: res.reason };
  const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || []);
  const normalised = list.map(normaliseCategory).filter(Boolean);
  if (!normalised.length) {
    return { source: 'static', categories: STATIC_CATEGORIES, reason: 'empty' };
  }
  return { source: 'api', categories: normalised, reason: null };
};

export const fetchTreatments = async () => {
  // Try a generous single-page fetch first. If the API supports pagination,
  // most public catalogs cap below 200 — we ask for 500 to be safe.
  const res = await apiGet('/treatments', { params: { limit: 500, page_size: 500 } });
  if (!res.ok) return { source: 'static', treatments: STATIC_TREATMENTS, reason: res.reason };
  const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || []);
  const normalised = list.map(normaliseTreatment).filter(Boolean);
  if (!normalised.length) {
    return { source: 'static', treatments: STATIC_TREATMENTS, reason: 'empty' };
  }
  return { source: 'api', treatments: normalised, reason: null };
};

/**
 * Unwrap an API envelope and return the overrides payload.
 *
 *   Handles these shapes (in priority order):
 *     • Raw payload                     → { treatments: {...} }
 *     • Single envelope                 → { success, data: { treatments: {...} } }
 *     • Double envelope                 → { success, data: { web_content_overrides: {...} } }
 *     • Direct nested key               → { web_content_overrides: {...} }
 *     • CamelCase                       → { webContentOverrides: {...} }
 *     • Generic 'overrides' key         → { overrides: {...} }
 *     • APIs without success flag       → { data: {...} }
 *
 *   Rules:
 *     • Return {} when `raw` is null / undefined / array / non-plain-object.
 *     • Return immediately if the node already contains any known surface key:
 *       treatments, categoryCards, popularTreatments, home, treatmentGuides,
 *       travelHub, branding.
 *     • Otherwise try to follow ONE wrapper key down. If none is a plain
 *       object, return {} — never a meaningless envelope node.
 *     • Bounded to 6 iterations to defuse pathological inputs.
 */
const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

const PAYLOAD_KEYS = [
  'treatments',
  'categoryCards',
  'popularTreatments',
  'home',
  'treatmentGuides',
  'travelHub',
  'branding',
];

const unwrapOverridesEnvelope = (raw) => {
  if (!isPlainObject(raw)) return {};

  let node = raw;
  for (let i = 0; i < 6; i += 1) {
    if (!isPlainObject(node)) return {};

    // Final payload — at least one known surface key is present.
    if (PAYLOAD_KEYS.some((k) => k in node)) return node;

    // Try wrapper keys in priority order.
    let next = null;
    if (isPlainObject(node.web_content_overrides)) {
      next = node.web_content_overrides;
    } else if (isPlainObject(node.webContentOverrides)) {
      next = node.webContentOverrides;
    } else if (isPlainObject(node.overrides)) {
      next = node.overrides;
    } else if (node.success === true && isPlainObject(node.data)) {
      next = node.data;
    } else if (!('success' in node) && isPlainObject(node.data)) {
      next = node.data;
    }

    // Nothing valid to unwrap → empty envelope, not a meaningless shell.
    if (!next) return {};
    node = next;
  }
  // Loop bound exhausted — only return if we landed on a payload-shaped node.
  return isPlainObject(node) && PAYLOAD_KEYS.some((k) => k in node) ? node : {};
};

export const fetchOverrides = async () => {
  const res = await apiGet('/settings/web-content-overrides');
  if (!res.ok) return { overrides: {}, reason: res.reason };
  const overrides = unwrapOverridesEnvelope(res.data);
  return { overrides, reason: null };
};

// Exported for unit tests / dev tools.
export { unwrapOverridesEnvelope };

// ── Hybrid merge for the detail page ────────────────────────────────────────

/**
 * Build a final treatment record for the detail page:
 *   • API record is the base (most authoritative).
 *   • For long-form blocks the API has not populated yet, supplement from the
 *     static catalog entry with the same slug (preserves SEO + FAQ depth).
 *   • If overrides[slug] exists, deep-merge those fields last (admin wins).
 */
export const mergeTreatment = (apiTreatment, staticTreatment, overrides) => {
  const base = apiTreatment || staticTreatment;
  if (!base) return null;

  const supplement = staticTreatment && apiTreatment && apiTreatment.slug === staticTreatment.slug
    ? staticTreatment
    : null;

  const LONG_FORM = [
    'whatIs', 'whoFor', 'benefits', 'procedure', 'recovery', 'risks',
    'stayInVietnam', 'priceGuide', 'pricingFactors', 'whyVietnam',
    'whyVietClinics', 'faq', 'seo', 'related', 'image', 'accent',
    'startingPrice', 'recoveryTime',
  ];

  const merged = { ...base };

  if (supplement) {
    LONG_FORM.forEach((key) => {
      const v = merged[key];
      const isEmpty =
        v === null ||
        v === undefined ||
        v === '' ||
        (Array.isArray(v) && v.length === 0);
      if (isEmpty && supplement[key] != null) {
        merged[key] = supplement[key];
      }
    });
  }

  // Admin overrides — keyed by treatment slug → object of field overrides.
  const slugOverride = overrides && overrides.treatments && overrides.treatments[merged.slug];
  if (slugOverride && typeof slugOverride === 'object') {
    Object.entries(slugOverride).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        merged[k] = v;
      }
    });
  }

  return merged;
};

export const STATIC_CATALOG = {
  categories: STATIC_CATEGORIES,
  treatments: STATIC_TREATMENTS,
};

export { isApiConfigured };
